/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import BenchlingEntityLink from '@/components/UI/BenchlingEntityLink/BenchlingEntityLink';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import Link from '@/components/UI/Link';
import type { FilterOption, TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import ApiAvatar from '@/components/UI/ApiAvatar/ApiAvatar';
import UserAvatar from '@/components/UI/UserAvatar';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { fetchMetadataColumns, formatNumber } from '@/helpers';
import { _isEmpty, _isEmptyString, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { MetadataFieldTypes, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { Sample, SampleMeasurement } from '@/model/Sample.model';
import { getSampleStatusDisplay } from '@/model/Sample.model';
import type { BaseUser } from '@/model/User.model';
import { web as webRoute } from '@/support/route';
import { DateRenderFormat, DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils';
import { type Study } from '@/model/Study.model.ts';
import { containsOperation, notContainsOperation, selectOperations } from '@/utils/FiltersOperations.utils.ts';
import { type StudyApiId } from '@/model/Study.model';
import { RouteService } from '@/support/RouteService';
import { getJwt } from '@/littledash';
import { errorToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { plannedSampleExportType } from '@/model/SamplePlan.model';
import { allSamplesSynced } from '@/components/Modals/CreateBenchlingWorklist/CreateBenchlingWorklist.utils';
import type { ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList';
import {
  BENCHLING_DELEGATED_ACTION_ICON,
  benchlingLinkDisabledReason,
  type BenchlingLinkStatus,
} from '@/support/benchlingLink/BenchlingLink.utils';
import { modalAction } from '@/utils/modal';
import type { Dispatch } from 'redux';
import {
  type AddSamplePlanContext,
  isSampleData,
  type SampleData,
  type TempSampleData,
} from '@/components/Samples/AddSamplePlanProvider.tsx';
import { type TeamGlossaryApiId } from '@/model/Glosary.model.ts';
import { type TempSampleTypeId } from '@/model/SamplePlan.model';

export interface SampleExtended extends Sample {
  study_link: Study & {
    access: string;
  };
}

const accessSet = new Set(['read', 'write']);

export const buildColumns = (
  groupMetadata: Array<MetadataFieldWithValue> = [],
  sampleMetadata: Array<MetadataFieldWithValue> = [],
  isStudy: boolean,
  plannedSamplesEnabled = false,
  sampleNamingEnabled = false
): Array<TanStackColumnDef<Sample>> => {
  const cellWidth = 100;

  const statusColumn: TanStackColumnDef<Sample> = {
    id: 'status',
    header: 'Sample Status',
    accessorKey: 'status',
    width: cellWidth,
    cell: ({ row: { original } }: { row: { original: Sample } }) => getSampleStatusDisplay(original?.status),
    isVisible: true,
  };

  const nameColumn: TanStackColumnDef<Sample> = {
    id: 'sample_name',
    header: 'Sample Name',
    accessorKey: 'sample_name',
    width: cellWidth,
    cell: ({ row: { original } }: { row: { original: Sample } }) => original.sample_name ?? '-',
    isVisible: true,
  };

  const timepointColumn: TanStackColumnDef<Sample> = {
    id: 'timepoint',
    header: 'Timepoint',
    accessorKey: 'timepoint',
    width: cellWidth,
    cell: ({ row: { original } }: { row: { original: Sample } }) =>
      DateUtils.renderDuration(original?.timepoint, { format: 'short', zeros: true, defaultResponse: '-' }),
    isVisible: true,
  };

  let tableColumns: Array<TanStackColumnDef<Sample>> = [
    {
      id: 'sample_id',
      header: 'Sample ID',
      accessorKey: 'sample_id',
      size: cellWidth,
      cell: ({ row: { original } }: { row: { original: Sample } }) => (
        <div className="flex items-center">
          <BenchlingEntityLink
            entityType="samples"
            apiId={original.api_id}
            benchlingEntityWebUrl={original.benchling_entity_web_url}
            visible={original.synced_to_benchling === true}
          />
          <span>{original?.sample_id ?? '-'}</span>
        </div>
      ),
      isVisible: true,
    },
    ...(sampleNamingEnabled ? [nameColumn] : []),
    {
      id: 'benchling_entity_id',
      header: 'Registry API ID',
      accessorKey: 'benchling_entity_id',
      size: cellWidth,
      cell: ({ row: { original } }: { row: { original: Sample } }) => original?.benchling_entity_id ?? '-',
      isVisible: false,
    },
    ...(plannedSamplesEnabled ? [statusColumn] : []),
    {
      id: 'subject',
      header: 'Animal',
      accessorKey: 'subject',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => {
        const { original } = row;
        if (_isNil(original?.subject?.id)) {
          return '-';
        }
        return accessSet.has((original as SampleExtended)?.study_link.access) ? (
          <Link
            className="link blue"
            to={webRoute('animals.show', {
              id: original.study_id,
              subject_id: original?.subject?.id,
            })}
          >
            {original?.subject?.name}
          </Link>
        ) : (
          original?.subject?.name
        );
      },
      isVisible: true,
    },
    {
      id: 'tail',
      header: 'Tail',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.tail,
      accessorPath: 'subject.alt_ids.tail',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.tail ?? '-',
      isVisible: false,
    },
    {
      id: 'donor',
      header: 'Donor',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.donor,
      accessorPath: 'subject.alt_ids.donor',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.donor ?? '-',
      isVisible: false,
    },
    {
      id: 'ear',
      header: 'Ear',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.ear,
      accessorPath: 'subject.alt_ids.ear',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.ear ?? '-',
      isVisible: false,
    },
    {
      id: 'tag',
      header: 'Tag',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.tag,
      accessorPath: 'subject.alt_ids.tag',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.tag ?? '-',
      isVisible: false,
    },
    {
      id: 'collection',
      header: 'Cage',
      accessorKey: 'collection',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.collection?.name ?? '-',
      isVisible: true,
    },
    {
      id: 'date',
      header: 'Date taken',
      accessorKey: 'date',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => (
        <DateTimeRenderer value={row.original?.date} format={DateRenderFormat.Date} defaultResponse="-" />
      ),
      isVisible: true,
    },
    {
      id: 'time',
      header: 'Time taken',
      accessorFn: (row: Sample) => row.date,
      accessorPath: 'time',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => (
        <DateTimeRenderer value={row.original?.date} format={DateTimeRenderFormat.Time} defaultResponse="-" />
      ),
      isVisible: true,
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.type ?? '-',
      isVisible: true,
    },
    {
      id: 'user',
      header: 'User',
      accessorKey: 'user',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) =>
        _notNil(row.original?.user) ? (
          <UserAvatar user={row.original.user} tooltip={row.original.user.name ?? row.original.user.email} />
        ) : (
          <ApiAvatar />
        ),
      isVisible: true,
    },
    {
      id: 'study_group',
      header: 'Group',
      accessorKey: 'study_group',
      size: 150,
      meta: { style: { overflow: 'visible' } },
      cell: ({ row }: { row: { original: Sample } }) => <GroupLabel group={row.original?.study_group} />,
      isVisible: true,
    },
    ...(plannedSamplesEnabled ? [timepointColumn] : []),
  ];

  if (isStudy) {
    tableColumns = [
      ...tableColumns,
      {
        id: 'comments',
        header: 'Comments',
        accessorKey: 'comments',
        enableSorting: false,
        size: cellWidth,
        cell: ({ row }: { row: { original: Sample } }) => row.original?.comments ?? '-',
        isVisible: false,
      },
      {
        id: 'weight',
        header: 'Weight',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.weight',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'weight'),
        isVisible: false,
      },
      {
        id: 'volume',
        header: 'Volume',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.volume',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'volume'),
        isVisible: false,
      },
      {
        id: 'length',
        header: 'Length',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.length',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'length'),
        isVisible: false,
      },
      {
        id: 'depth',
        header: 'Depth',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.depth',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'depth'),
        isVisible: false,
      },
      {
        id: 'width',
        header: 'Width',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.width',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'width'),
        isVisible: false,
      },
    ];
  } else {
    tableColumns = [
      ...tableColumns,
      ...fetchMetadataColumns({
        metadata: groupMetadata,
        nestedStudyLocation: 'study_group',
      }),
      {
        id: 'study',
        header: 'Study',
        accessorKey: 'study',
        enableSorting: false,
        size: cellWidth,
        cell: ({ row }: { row: { original: Sample } }) => {
          const { original } = row;
          if (_isNil(original['study_link']?.id)) {
            return '-';
          }
          return accessSet.has((original as SampleExtended)?.study_link.access) ? (
            <Link
              className="link blue"
              to={webRoute('studies.show', {
                id: original.study_link?.id,
              })}
            >
              {original.study_link?.name}
            </Link>
          ) : (
            original.study_link?.name
          );
        },
        isVisible: true,
      },
      {
        id: 'maxSubjects',
        header: 'Max Subjects',
        accessorKey: 'max_subjects',
        enableSorting: false,
        size: cellWidth,
        cell: ({ row }: { row: { original: Sample } }) => {
          return row.original?.study_group?.max_subjects ?? '-';
        },
        isVisible: true,
      },
    ];
  }

  return [...tableColumns, ...fetchMetadataColumns({ metadata: sampleMetadata })];
};

const processDetails = (details: Array<SampleMeasurement>, key: string) => {
  const detailsInstance = details?.find((x) => x.key === key);
  if (_isEmpty(detailsInstance) || _isNil(detailsInstance?.value) || _isEmptyString(detailsInstance?.value)) {
    return '-';
  }
  return `${formatNumber(detailsInstance.value)} ${detailsInstance?.unit}`;
};

export const generateFilterOptions = (
  users: Array<BaseUser>,
  studyId: ID,
  metadata: Array<MetadataFieldWithValue>,
  plannedSamplesEnabled = false
) => {
  const mapped = users.map(({ name, id: value }) => ({
    name,
    value,
  }));

  const statusFilter: FilterOption = {
    value: 'status',
    name: 'Status',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: [
          { name: 'Planned', value: 'planned' },
          { name: 'Collected', value: 'collected' },
          { name: 'Cancelled', value: 'cancelled' },
        ],
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: [
          { name: 'Planned', value: 'planned' },
          { name: 'Collected', value: 'collected' },
          { name: 'Cancelled', value: 'cancelled' },
        ],
      },
    ],
  };

  const timepointFilter: FilterOption = {
    value: 'timepoint',
    name: 'Timepoint',
    operations: [
      {
        name: filterType.eq_interval,
        value: filterType.eq_interval,
      },
      {
        name: filterType.gt_interval,
        value: filterType.gt_interval,
      },
      {
        name: filterType.lt_interval,
        value: filterType.lt_interval,
      },
      {
        name: filterType.between_interval,
        value: filterType.between_interval,
      },
    ],
  };

  const filterOptions: Array<FilterOption> = [
    {
      value: 'type',
      name: 'Type',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
      ],
      fetchFromApi: {
        placeholder: 'Sample type',
        url: 'team-glossary.list',
        queryParams: { group: 'samples' },
        tableFilterMenuProperty: 'team_glossary_api_id',
      },
    },
    {
      value: 'user',
      name: 'User',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: mapped,
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: mapped,
        },
      ],
    },
    ...(plannedSamplesEnabled ? [statusFilter, timepointFilter] : []),
  ];

  if (_notNil(studyId)) {
    filterOptions.push({
      value: 'study_group',
      name: 'Study Group',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
        {
          name: filterType.IS_NULL,
          value: filterType.IS_NULL,
        },
        {
          name: filterType.IS_NOT_NULL,
          value: filterType.IS_NOT_NULL,
        },
      ],
      fetchFromApi: {
        placeholder: 'Search groups',
        url: 'studies.groups.list',
        params: {
          id: studyId,
        },
      },
    });
  } else {
    filterOptions.push({
      value: 'study',
      name: 'Study',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
      ],
      fetchFromApi: {
        placeholder: 'Search studies',
        url: 'studies.search',
      },
    });
  }

  if (_notNil(metadata)) {
    const metadataFilters =
      metadata?.map(
        (item: { id: ID; title: string; field_type: MetadataFieldTypes; options?: string[] }): FilterOption => {
          switch (item.field_type) {
            case 'date':
              return {
                value: item.id,
                name: item.title,
                operations: [
                  {
                    name: filterType.contains,
                    value: filterType.eq_date,
                  },
                  {
                    name: filterType.not_contains,
                    value: filterType.not_eq_date,
                  },
                ],
              };
            case 'multi_select': {
              return {
                value: item.id,
                name: item.title,
                operations: selectOperations,
                fetchFromApi: {
                  placeholder: 'Multiselect options',
                  url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
                  params: { metadataGlossaryId: item.id },
                  fieldType: 'multi_select',
                  useModernApi: true,
                },
              };
            }
            case 'select': {
              return {
                value: item.id,
                name: item.title,
                operations: selectOperations,
                fetchFromApi: {
                  placeholder: 'Select options',
                  url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
                  params: { metadataGlossaryId: item.id },
                  fieldType: 'select',
                  useModernApi: true,
                },
              };
            }

            default:
              return {
                value: item.id,
                name: item.title,
                operations: [...containsOperation, ...notContainsOperation],
              };
          }
        }
      ) ?? [];

    metadataFilters.forEach((filter) => filterOptions.push(filter));
  }

  return filterOptions;
};

export const validateManageSamplesSelection = (
  hasAccess: boolean,
  selectedSamples: Array<Sample> | undefined = []
): { disabled: boolean; tooltip: string } => {
  const defaultResult = {
    disabled: false,
    tooltip: '',
  };

  if (_isEmpty(selectedSamples)) {
    return defaultResult;
  }

  if (_isNotEmpty(selectedSamples)) {
    if (!hasAccess) {
      return {
        disabled: true,
        tooltip: 'You must have at least write access to the study to manage samples',
      };
    } else if (new Set(selectedSamples.map((x) => x.type)).size > 1) {
      return {
        disabled: true,
        tooltip: 'You cannot edit more than one type of sample at a time',
      };
    }
  }
  return defaultResult;
};

export const validateCancelSamplesSelection = (
  hasAccess: boolean,
  selectedSamples: Array<Sample> | undefined = []
): { disabled: boolean; tooltip: string } => {
  if (_isEmpty(selectedSamples)) {
    return {
      disabled: false,
      tooltip: '',
    };
  }

  if (!hasAccess) {
    return {
      disabled: true,
      tooltip: 'You must have at least write access to the study to cancel samples',
    };
  }

  // Check if all selected samples have planned status
  const nonPlannedSamples = selectedSamples.filter((sample) => sample.status !== 'planned');

  if (_isNotEmpty(nonPlannedSamples)) {
    return {
      disabled: true,
      tooltip: 'All selected samples must have planned status to cancel them',
    };
  }

  return {
    disabled: false,
    tooltip: '',
  };
};

/**
 * The samples table's counterpart to the animals table's worklist action, gated the same way: disabled
 * with the reason in a tooltip when the user has no usable link, or the selection contains a sample
 * that has not synced. The link comes first, since syncing more samples cannot help without it. Absent
 * entirely when the tenant flag is off, or outside a study.
 */
export const sampleBenchlingWorklistActions = (
  benchlingWorklistEnabled: boolean,
  samples: Array<Sample>,
  studyApiId: StudyApiId | undefined,
  benchlingLinkStatus: BenchlingLinkStatus,
  dispatch: Dispatch,
  onSuccess: () => void
): Array<ActionProps> => {
  if (!benchlingWorklistEnabled || _isNil(studyApiId)) {
    return [];
  }
  const linkReason = benchlingLinkDisabledReason(benchlingLinkStatus, 'create a worklist');
  const syncReason = allSamplesSynced(samples)
    ? undefined
    : 'All selected samples must be synced to Benchling before they can be added to a worklist.';
  const tooltip = linkReason ?? syncReason;

  return [
    {
      name: 'Add to worklist',
      key: 'create_benchling_worklist',
      tooltip,
      icon: BENCHLING_DELEGATED_ACTION_ICON,
      disabled: _notNil(tooltip),
      action: () => modalAction(dispatch).openModal('CREATE_BENCHLING_WORKLIST', { studyApiId, samples, onSuccess }),
    },
  ];
};

export type SampleGlossaryEntry = AddSamplePlanContext['sampleGlossary'] extends Map<any, infer V> ? V : never;

/**
 * Checks if the current sample type is archived
 */
export const isCurrentSampleArchived = (
  sampleData: SampleData | TempSampleData | null,
  sampleGlossary: Map<TeamGlossaryApiId, SampleGlossaryEntry>
): boolean => {
  // TempSampleData carries a tgl_ temp id; only tgy_ ids are real sample types
  if (!sampleData || !isSampleData(sampleData)) {
    return false;
  }

  const glossaryEntry = sampleGlossary.get(sampleData.type);
  return _notNil(glossaryEntry?.archived_at);
};

/**
 * Checks if any planned samples contain archived sample types
 */
export const containsArchivedSampleTypes = (
  plannedSamples: ReadonlyMap<TeamGlossaryApiId | TempSampleTypeId, SampleData | TempSampleData>,
  sampleGlossary: Map<TeamGlossaryApiId, SampleGlossaryEntry>
): boolean => {
  return Array.from(plannedSamples.values()).some((sampleData) => isCurrentSampleArchived(sampleData, sampleGlossary));
};

/**
 * Fetches the sampling plan CSV for the given study and triggers a browser file download.
 */
export const downloadSamplingPlan = async (studyApiId: StudyApiId): Promise<void> => {
  try {
    const apiRoute = RouteService.api({
      endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/export/{type}',
      path: { studyApiId, type: plannedSampleExportType.sample_plan },
    });
    const response = await fetch(
      new Request(apiRoute.url, {
        method: apiRoute.method,
        headers: { Authorization: `Bearer ${getJwt()}` },
      })
    );

    if (!response.ok) {
      errorToast('Failed to export sample plan.');
      return;
    }

    const filename =
      /filename="(?<filename>[a-zA-Z0-9_.-]+)"/.exec(response.headers.get('content-disposition') ?? '')?.groups
        ?.filename ?? 'sampling-plan.csv';
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  } catch (error) {
    ExceptionHandler.captureException(
      new InVivoError('Sample plan export failed', { cause: error, slug: 'sample-plan-export' })
    );
    errorToast('Failed to export sample plan.');
  }
};
